/**
 * The per-tab store can outlive the backend's willingness to honour it: the TTL runs out, the kill switch is turned
 * off, or the backend ignores or refuses the header. This hook keeps the store honest so a stale target never keeps a
 * tab in a half-entered session: it ends the session the moment its TTL runs out, restarts as the signed-in admin
 * once `/me` or my-permissions refuses the header, and does the same once two responses from either of them arrive
 * without the echo for an entry that is still stored.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { navigateHome } from 'src/lib/view-as/navigate-home';
import { getViewAsRefusalCode, readViewAsEcho } from 'src/lib/view-as/view-as-response';
import { clearViewAs, getViewAsTarget, lapseExpiredViewAs } from 'src/lib/view-as/view-as-store';
import { useAppControllerGetMeQuery, useMyPermissionsControllerGetMyPermissionsQuery } from 'src/store/leela-api';
import { rtkQueryBase } from 'src/store/rtk-query-base';

const MISSING_ECHO_RESPONSES_BEFORE_RESET = 2;

interface EchoQueryResult {
  readonly data?: unknown;
  readonly error?: unknown;
  readonly fulfilledTimeStamp?: number;
}

interface EchoResponseHandlers {
  readonly onResponse: (hasEcho: boolean) => void;
  readonly onCacheReset: () => void;
}

/** Calls onResponse once per settled response, and onCacheReset when a settled cache entry is cleared. */
function useEchoResponses (
  { data, fulfilledTimeStamp }: EchoQueryResult,
  { onResponse, onCacheReset }: EchoResponseHandlers,
): void {
  const lastCountedTimeStamp = useRef<number>();

  useEffect(() => {
    if (fulfilledTimeStamp === undefined) {
      if (lastCountedTimeStamp.current !== undefined) {
        lastCountedTimeStamp.current = undefined;
        onCacheReset();
      }
      return;
    }
    if (fulfilledTimeStamp === lastCountedTimeStamp.current) {
      return;
    }
    lastCountedTimeStamp.current = fulfilledTimeStamp;
    onResponse(readViewAsEcho(data) !== undefined);
  }, [data, fulfilledTimeStamp, onCacheReset, onResponse]);
}

export function useViewAsSessionCheck (): void {
  const dispatch = useDispatch();
  const me = useAppControllerGetMeQuery();
  const permissions = useMyPermissionsControllerGetMyPermissionsQuery();
  const missingEchoCount = useRef(0);
  const hasSeenEcho = useRef(false);
  const hasReset = useRef(false);
  // Reading the store also drops an entry that expired before this document used it, so no request carries it.
  const storedExpiresAt = getViewAsTarget()?.expiresAt;

  const restartApp = useCallback((): void => {
    if (hasReset.current) {
      return;
    }
    hasReset.current = true;
    dispatch(rtkQueryBase.util.resetApiState());
    navigateHome();
  }, [dispatch]);

  const restartAsAdmin = useCallback((): void => {
    if (hasReset.current) {
      return;
    }
    clearViewAs();
    restartApp();
  }, [restartApp]);

  const onResponse = useCallback((hasEcho: boolean): void => {
    if (hasEcho) {
      hasSeenEcho.current = true;
      missingEchoCount.current = 0;
      return;
    }
    if (getViewAsTarget() === undefined) {
      missingEchoCount.current = 0;
      if (hasSeenEcho.current) {
        restartAsAdmin();
      }
      return;
    }
    missingEchoCount.current += 1;
    if (missingEchoCount.current >= MISSING_ECHO_RESPONSES_BEFORE_RESET) {
      restartAsAdmin();
    }
  }, [restartAsAdmin]);

  // A cache reset means whoever cleared the session already restarted the app.
  const onCacheReset = useCallback((): void => {
    hasSeenEcho.current = false;
  }, []);

  useEffect(() => {
    if (storedExpiresAt === undefined) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      // Timers can fire a millisecond early; the entry is due by now either way.
      if (lapseExpiredViewAs(Math.max(Date.now(), storedExpiresAt))) {
        restartApp();
      }
    }, storedExpiresAt - Date.now());
    return () => {
      window.clearTimeout(timer);
    };
  }, [restartApp, storedExpiresAt]);

  const isHeaderRefused =
    getViewAsRefusalCode(me.error) !== undefined || getViewAsRefusalCode(permissions.error) !== undefined;
  useEffect(() => {
    if (isHeaderRefused && getViewAsTarget() !== undefined) {
      restartAsAdmin();
    }
  }, [isHeaderRefused, restartAsAdmin]);

  const handlers = { onCacheReset, onResponse };
  useEchoResponses(me, handlers);
  useEchoResponses(permissions, handlers);
}
